import type { Locale } from "@/lib/i18n/config";
import { ACHIEVEMENTS, type HeartopiaAchievement } from "@/features/achievements/data";
import type { FaqItem } from "@/components/detail/FaqSection";
import { ACHIEVEMENT_TRANSLATIONS_ID } from "@/features/achievements/content.id";
import { ACHIEVEMENT_TRANSLATIONS_TH } from "@/features/achievements/content.th";
import { ACHIEVEMENT_TRANSLATIONS_DE } from "@/features/achievements/content.de";

interface AchievementTranslation {
  name: string;
  description: string;
  titleGained?: string;
  hint?: string;
}

type TranslatedLocale = Exclude<Locale, "en">;

const ACHIEVEMENT_TRANSLATIONS_BASE: Record<string, Partial<Record<TranslatedLocale, AchievementTranslation>>> = {
  "never-empty-handed": {
    "pt-BR": {
      name: "Nunca de Mãos Vazias",
      description: "A Hobby de Pesca atinge o Nv. 10",
      titleGained: "Pescador Habilidoso",
    },
    es: {
      name: "Nunca con las Manos Vacías",
      description: "La afición de Pesca alcanza el Nv. 10",
      titleGained: "Pescador Habilidoso",
    },
  },
  "mystic-fisher": {
    "pt-BR": {
      name: "Pescador Místico",
      description: "Ative a fase oculta do Evento de Pesca 10 vezes no total.",
      titleGained: "Pescador",
    },
    es: {
      name: "Pescador Místico",
      description: "Activa la fase oculta del Evento de Pesca 10 veces en total.",
      titleGained: "Pescador",
    },
  },
  "shoals-blessing": {
    "pt-BR": {
      name: "Bênção do Cardume",
      description: "Ative 100 Cardumes no total durante Eventos de Pesca.",
      titleGained: "Bênção",
    },
    es: {
      name: "Bendición del Banco",
      description: "Activa 100 Bancos de peces en total durante Eventos de Pesca.",
      titleGained: "Bendición",
    },
  },
  "strong-sailor": {
    "pt-BR": {
      name: "Marinheiro Forte",
      description: "Pesque peixes com mais de 100kg por 2 vezes consecutivas.",
      titleGained: "Linha de Pesca",
    },
    es: {
      name: "Marinero Fuerte",
      description: "Pesca peces de más de 100 kg 2 veces consecutivas.",
      titleGained: "Sedal",
    },
  },
  "twin-fish-fortune": {
    "pt-BR": {
      name: "Fortuna de Peixes Gêmeos",
      description: "Pesque 2 peixes 5 estrelas em até 1 minuto.",
      titleGained: "Águia-Pescadora",
    },
    es: {
      name: "Fortuna de Peces Gemelos",
      description: "Pesca 2 peces de 5 estrellas en menos de 1 minuto.",
      titleGained: "Águila Pescadora",
    },
  },
  "fishing-machine": {
    "pt-BR": {
      name: "Máquina de Pescar",
      description: "Pesque um total de 50 peixes com mais de 100kg cada.",
      titleGained: "Cesto de Peixes",
    },
    es: {
      name: "Máquina de Pesca",
      description: "Pesca un total de 50 peces de más de 100 kg cada uno.",
      titleGained: "Cesta de Peces",
    },
  },
  "shark-frenzy": {
    "pt-BR": {
      name: "Frenesi de Tubarões",
      description: "Pesque 3 tubarões a partir de sombras douradas durante um único Evento de Pesca no Mar.",
      titleGained: "Gaiola de Tubarão",
    },
    es: {
      name: "Frenesí de Tiburones",
      description: "Pesca 3 tiburones a partir de sombras doradas durante un único Evento de Pesca en el Mar.",
      titleGained: "Jaula de Tiburón",
    },
  },
  "shoal-caller": {
    "pt-BR": {
      name: "Chamador de Cardumes",
      description: "Ative 3 Cardumes em um único Evento de Pesca.",
      titleGained: "Chamador de Cardumes",
    },
    es: {
      name: "Llamador de Bancos",
      description: "Activa 3 Bancos de peces en un único Evento de Pesca.",
      titleGained: "Llamador de Bancos",
    },
  },
  "starlight-fisher": {
    "pt-BR": {
      name: "Pescador Estelar",
      description: "Pesque 4 peixes 5 estrelas em um único Evento de Pesca.",
      titleGained: "Maníaco da Pesca",
    },
    es: {
      name: "Pescador Estelar",
      description: "Pesca 4 peces de 5 estrellas en un único Evento de Pesca.",
      titleGained: "Maníaco de la Pesca",
    },
  },
  "town-cooking-legend": {
    "pt-BR": {
      name: "Lenda da Culinária da Cidade",
      description: "A Hobby de Culinária atinge o Nv. 10.",
      titleGained: "Chef",
    },
    es: {
      name: "Leyenda de la Cocina del Pueblo",
      description: "La afición de Cocina alcanza el Nv. 10.",
      titleGained: "Chef",
    },
  },
  "fast-and-flawless": {
    "pt-BR": {
      name: "Rápido e Impecável",
      description: "Cozinhe 2 pratos 5 estrelas em até 60s.",
      titleGained: "Relógio de Cozinha",
    },
    es: {
      name: "Rápido e Impecable",
      description: "Cocina 2 platos de 5 estrellas en menos de 60s.",
      titleGained: "Reloj de Cocina",
    },
  },
  "green-touch": {
    "pt-BR": { name: "Toque Verde", description: "A Hobby de Jardinagem atinge o Nv. 10.", titleGained: "Horticultor" },
    es: { name: "Toque Verde", description: "La afición de Jardinería alcanza el Nv. 10.", titleGained: "Horticultor" },
  },
  "plentiful-harvest": {
    "pt-BR": {
      name: "Colheita Abundante",
      description: "Colha cultivos 5 estrelas enquanto a Colheita Bônus está ativa.",
      titleGained: "Colheita",
    },
    es: {
      name: "Cosecha Abundante",
      description: "Cosecha cultivos de 5 estrellas mientras la Cosecha Bono está activa.",
      titleGained: "Cosecha",
    },
  },
  "rainbow-luck": {
    "pt-BR": {
      name: "Sorte Arco-Íris",
      description: "Ative 2 Bênçãos Híbridas Arco-Íris em uma única rega de múltiplas áreas.",
      titleGained: "Sorte Arco-Íris",
    },
    es: {
      name: "Suerte Arcoíris",
      description: "Activa 2 Bendiciones Híbridas Arcoíris en un único riego de múltiples áreas.",
      titleGained: "Suerte Arcoíris",
    },
  },
  "ace-cat-servant": {
    "pt-BR": {
      name: "Servo Ás dos Gatos",
      description: "A Hobby de Cuidado de Gatos atinge o Nv. 10.",
      titleGained: "Amante de Gatos",
    },
    es: {
      name: "Sirviente Estrella de Gatos",
      description: "La afición de Cuidado de Gatos alcanza el Nv. 10.",
      titleGained: "Amante de los Gatos",
    },
  },
  "meow-meow-canteen": {
    "pt-BR": {
      name: "Cantina Miau-Miau",
      description: "Alimente 5 gatos com a comida favorita deles.",
      titleGained: "Comida de Gato",
    },
    es: {
      name: "Cantina Miau-Miau",
      description: "Alimenta a 5 gatos con su comida favorita.",
      titleGained: "Comida para Gatos",
    },
  },
  "ace-dog-trainer": {
    "pt-BR": {
      name: "Treinador Ás de Cães",
      description: "A Hobby de Cuidado de Cães atinge o Nv. 10.",
      titleGained: "Amigo Canino",
    },
    es: {
      name: "Entrenador Estrella de Perros",
      description: "La afición de Cuidado de Perros alcanza el Nv. 10.",
      titleGained: "Amigo Canino",
    },
  },
  "doggie-canteen": {
    "pt-BR": {
      name: "Cantina Canina",
      description: "Alimente 3 cães com a comida favorita deles.",
      titleGained: "Comida de Cachorro",
    },
    es: {
      name: "Cantina Canina",
      description: "Alimenta a 3 perros con su comida favorita.",
      titleGained: "Comida para Perros",
    },
  },
  "insect-commander": {
    "pt-BR": {
      name: "Comandante dos Insetos",
      description: "A Hobby de Captura de Insetos atinge o Nv. 10.",
      titleGained: "Mestre dos Insetos",
    },
    es: {
      name: "Comandante de Insectos",
      description: "La afición de Caza de Insectos alcanza el Nv. 10.",
      titleGained: "Maestro de Insectos",
    },
  },
  "mystic-tracker": {
    "pt-BR": {
      name: "Rastreador Místico",
      description: "Ative a fase oculta no Evento de Captura de Insetos 10 vezes no total.",
      titleGained: "Exploração",
    },
    es: {
      name: "Rastreador Místico",
      description: "Activa la fase oculta en el Evento de Caza de Insectos 10 veces en total.",
      titleGained: "Exploración",
    },
  },
  "swarm-commander": {
    "pt-BR": {
      name: "Comandante de Enxames",
      description: "Ative 100 Enxames no total durante Eventos de Captura de Insetos.",
      titleGained: "Enxame",
    },
    es: {
      name: "Comandante de Enjambres",
      description: "Activa 100 Enjambres en total durante Eventos de Caza de Insectos.",
      titleGained: "Enjambre",
    },
  },
  "insect-harvester": {
    "pt-BR": {
      name: "Colhedor de Insetos",
      description: "Capture 3 insetos com uma única bolha de captura.",
      titleGained: "Captura Tripla",
    },
    es: {
      name: "Cosechador de Insectos",
      description: "Atrapa 3 insectos con una sola burbuja de captura.",
      titleGained: "Captura Triple",
    },
  },
  "five-insects-blessing": {
    "pt-BR": {
      name: "Bênção dos Cinco Insetos",
      description: "Capture 5 insetos 5 estrelas durante um único Evento de Captura de Insetos.",
      titleGained: "Senhor dos Insetos",
    },
    es: {
      name: "Bendición de los Cinco Insectos",
      description: "Atrapa 5 insectos de 5 estrellas durante un único Evento de Caza de Insectos.",
      titleGained: "Señor de los Insectos",
    },
  },
  "human-insect-attractor": {
    "pt-BR": {
      name: "Atraidor Humano de Insetos",
      description: "Ative 4 Enxames em um único Evento de Captura de Insetos.",
      titleGained: "Atraidor de Insetos",
    },
    es: {
      name: "Atractor Humano de Insectos",
      description: "Activa 4 Enjambres en un único Evento de Caza de Insectos.",
      titleGained: "Atractor de Insectos",
    },
  },
  "bird-whisperer": {
    "pt-BR": {
      name: "Sussurrador de Pássaros",
      description: "A Hobby de Observação de Pássaros atinge o Nv. 10.",
      titleGained: "Sussurrador de Pássaros",
    },
    es: {
      name: "Susurrador de Aves",
      description: "La afición de Observación de Aves alcanza el Nv. 10.",
      titleGained: "Susurrador de Aves",
    },
  },
  "cloud-walker": {
    "pt-BR": {
      name: "Andarilho das Nuvens",
      description: "Ative a fase oculta do Evento de Observação de Pássaros 10 vezes no total.",
      titleGained: "Céu",
    },
    es: {
      name: "Caminante de las Nubes",
      description: "Activa la fase oculta del Evento de Observación de Aves 10 veces en total.",
      titleGained: "Cielo",
    },
  },
  "harmony-with-breeze": {
    "pt-BR": {
      name: "Harmonia com a Brisa",
      description: "Ative 100 Ondas de Pássaros no total durante Eventos de Observação de Pássaros.",
      titleGained: "Ressonância",
    },
    es: {
      name: "Armonía con la Brisa",
      description: "Activa 100 Oleadas de Aves en total durante Eventos de Observación de Aves.",
      titleGained: "Resonancia",
    },
  },
  "joyful-chorus": {
    "pt-BR": {
      name: "Coro Alegre",
      description: "Ative 3 Ondas de Pássaros em um único Evento de Observação de Pássaros.",
      titleGained: "Pena",
    },
    es: {
      name: "Coro Alegre",
      description: "Activa 3 Oleadas de Aves en un único Evento de Observación de Aves.",
      titleGained: "Pluma",
    },
  },
  "decisive-moment": {
    "pt-BR": {
      name: "Momento Decisivo",
      description: "Obtenha 10 Cartões de Informação 5 estrelas em um único Evento de Observação de Pássaros.",
      titleGained: "Momento",
    },
    es: {
      name: "Momento Decisivo",
      description: "Obtén 10 Tarjetas de Información de 5 estrellas en un único Evento de Observación de Aves.",
      titleGained: "Momento",
    },
  },
  "sand-sculpture-artist": {
    "pt-BR": {
      name: "Artista de Esculturas de Areia",
      description: "A Hobby de Escultura de Areia atinge o Nv. 5.",
      titleGained: "Escultor de Areia",
    },
    es: {
      name: "Artista de Esculturas de Arena",
      description: "La afición de Escultura de Arena alcanza el Nv. 5.",
      titleGained: "Escultor de Arena",
    },
  },
  pumpkinarchy: {
    "pt-BR": {
      name: "Aboboarquia",
      description: "A Hobby de Escultura de Abóbora atinge o Nv. 5.",
      titleGained: "Cabeça de Abóbora",
    },
    es: {
      name: "Calabazocracia",
      description: "La afición de Talla de Calabazas alcanza el Nv. 5.",
      titleGained: "Cabeza de Calabaza",
    },
  },
  "snow-king": {
    "pt-BR": {
      name: "Rei da Neve",
      description: "A Hobby de Escultura de Neve atinge o Nv. 5.",
      titleGained: "Rei da Neve",
    },
    es: {
      name: "Rey de la Nieve",
      description: "La afición de Escultura de Nieve alcanza el Nv. 5.",
      titleGained: "Rey de la Nieve",
    },
  },
  collector: {
    "pt-BR": {
      name: "Colecionador",
      description: "Torne-se um Colecionador Especialista.",
      titleGained: "Colecionador",
    },
    es: {
      name: "Coleccionista",
      description: "Conviértete en un Coleccionista Experto.",
      titleGained: "Coleccionista",
    },
  },
  "rocket-sponsor": {
    "pt-BR": {
      name: "Patrocinador do Foguete",
      description: "Ganhe um total de 500.000 de Ouro vendendo itens ao Mercador de Ouro Albert Jr.",
      titleGained: "Foguete",
    },
    es: {
      name: "Patrocinador del Cohete",
      description: "Gana un total de 500.000 de Oro vendiendo artículos al Mercader de Oro Albert Jr.",
      titleGained: "Cohete",
    },
  },
  "dg-member": {
    "pt-BR": {
      name: "Membro D.G.",
      description: "O Nível de Membro D.G. atinge o Nv. 30.",
      titleGained: "Membro D.G.",
    },
    es: {
      name: "Miembro D.G.",
      description: "El Nivel de Miembro D.G. alcanza el Nv. 30.",
      titleGained: "Miembro D.G.",
    },
  },
  "puzzle-artist": {
    "pt-BR": {
      name: "Artista de Quebra-Cabeças",
      description: "Torne-se um Artista de Quebra-Cabeças.",
      titleGained: "Quebra-Cabeça",
    },
    es: {
      name: "Artista de Rompecabezas",
      description: "Conviértete en un Artista de Rompecabezas.",
      titleGained: "Rompecabezas",
    },
  },
  "persistent-quill": {
    "pt-BR": {
      name: "Pena Persistente",
      description: "O Sonho de Escrita atinge o nível Mestre.",
      titleGained: "Escrita",
    },
    es: {
      name: "Pluma Persistente",
      description: "El Sueño de Escritura alcanza el nivel Maestro.",
      titleGained: "Escritura",
    },
  },
  "golden-music-cd": {
    "pt-BR": {
      name: "CD de Música Dourado",
      description: "O Sonho da Música atinge o CD de Música Dourado.",
      titleGained: "Música",
    },
    es: {
      name: "CD de Música Dorado",
      description: "El Sueño de la Música alcanza el CD de Música Dorado.",
      titleGained: "Música",
    },
  },
  "animal-neighbour": {
    "pt-BR": {
      name: "Vizinho dos Animais",
      description: "Alcance o Nível de Vínculo 10 com 8 grupos de animais.",
      titleGained: "Novo Vizinho",
    },
    es: {
      name: "Vecino de los Animales",
      description: "Alcanza el Nivel de Vínculo 10 con 8 grupos de animales.",
      titleGained: "Nuevo Vecino",
    },
  },
  "stardust-collector": {
    "pt-BR": {
      name: "Colecionador de Poeira Estelar",
      description: "Colete 60 Fragmentos de Estrelas Cadentes.",
      titleGained: "Catador de Estrelas",
    },
    es: {
      name: "Coleccionista de Polvo Estelar",
      description: "Recolecta 60 Fragmentos de Estrellas Fugaces.",
      titleGained: "Recolector de Estrellas",
    },
  },
  "animal-keeper": {
    "pt-BR": {
      name: "Guardião dos Animais",
      description: "Descubra as comidas favoritas de 8 grupos de animais.",
      titleGained: "Delicioso",
    },
    es: {
      name: "Guardián de los Animales",
      description: "Descubre las comidas favoritas de 8 grupos de animales.",
      titleGained: "Delicioso",
    },
  },
  "ice-elf": {
    "pt-BR": {
      name: "Elfo do Gelo",
      description: "O Sonho da Patinação Artística atinge o Elfo do Gelo.",
      titleGained: "Patinação no Gelo",
    },
    es: {
      name: "Elfo de Hielo",
      description: "El Sueño de Patinaje Artístico alcanza el Elfo de Hielo.",
      titleGained: "Patinaje sobre Hielo",
    },
  },
  "mystery-onsen-buddy": {
    "pt-BR": { name: "Amigo do Onsen", description: "Entre no Onsen com um amigo." },
    es: { name: "Amigo del Onsen", description: "Entra al Onsen con un amigo." },
  },
  "mystery-friendship-under-the-meteor": {
    "pt-BR": {
      name: "Amizade Sob o Meteoro",
      description: "No Nível de Amizade 5, use a emote de Desejo com um amigo durante o clima de Chuva de Meteoros.",
    },
    es: {
      name: "Amistad Bajo el Meteoro",
      description:
        "En el Nivel de Amistad 5, usa el emote de Deseo con un amigo durante el clima de Lluvia de Meteoros.",
    },
  },
  "mystery-super-star": {
    "pt-BR": { name: "Superestrela", description: "Obtenha 100 curtidas na sua casa." },
    es: { name: "Superestrella", description: "Obtén 100 me gusta en tu casa." },
  },
  "mystery-repair-expert": {
    "pt-BR": { name: "Especialista em Reparos", description: "Compartilhe 100 kits de reparo." },
    es: { name: "Experto en Reparaciones", description: "Comparte 100 kits de reparación." },
  },
  "mystery-food-diplomat": {
    "pt-BR": { name: "Diplomata da Comida", description: "Compartilhe comida 100 vezes." },
    es: { name: "Diplomático de la Comida", description: "Comparte comida 100 veces." },
  },
  "mystery-wholesaler-of-grasslands": {
    "pt-BR": { name: "Atacadista das Pradarias", description: "Compartilhe o arbusto de camuflagem 100 vezes." },
    es: { name: "Mayorista de las Praderas", description: "Comparte el arbusto de camuflaje 100 veces." },
  },
  "mystery-insect-hunting-party": {
    "pt-BR": { name: "Festa de Caça a Insetos", description: "Compartilhe o atraidor de insetos inflável 100 vezes." },
    es: { name: "Fiesta de Caza de Insectos", description: "Comparte el atractor de insectos inflable 100 veces." },
  },
  "mystery-rainbow-volunteer": {
    "pt-BR": { name: "Voluntário Arco-Íris", description: "Compartilhe o buquê arco-íris 50 vezes." },
    es: { name: "Voluntario Arcoíris", description: "Comparte el ramo arcoíris 50 veces." },
  },
  "onsen-buddy": {
    "pt-BR": {
      name: "Amigo do Onsen",
      description: "Tome um Onsen com amigos.",
      titleGained: "Onsen",
      hint: "Em meio ao vapor que dança, a amizade se aprofunda em silêncio.",
    },
    es: {
      name: "Amigo del Onsen",
      description: "Toma un Onsen con amigos.",
      titleGained: "Onsen",
      hint: "Entre el vapor que se arremolina, la amistad se profundiza en silencio.",
    },
  },
  "mystery-best-selling-author": {
    "pt-BR": { name: "Autor Best-Seller", description: "Venda um total de 1000 livros." },
    es: { name: "Autor Best-Seller", description: "Vende un total de 1000 libros." },
  },
  "mystery-great-author": {
    "pt-BR": { name: "Grande Autor", description: "Escreva pelo menos 10 livros, cada um com 200 vendas." },
    es: { name: "Gran Autor", description: "Escribe al menos 10 libros, cada uno con 200 ventas." },
  },
  "mystery-bookcase-full": {
    "pt-BR": {
      name: "Estante Cheia (Não Confirmado)",
      description: "Não confirmado: a dica menciona sua estante ficando cheia.",
    },
    es: {
      name: "Estantería Llena (Sin Confirmar)",
      description: "Sin confirmar: la pista menciona que tu estantería se llena.",
    },
  },
  "mystery-sea-fishing-grand-slam": {
    "pt-BR": {
      name: "Grand Slam da Pesca no Mar",
      description: "Colete todos os títulos de fim de evento do evento diário de Pesca no Mar.",
    },
    es: {
      name: "Grand Slam de la Pesca en el Mar",
      description: "Recolecta todos los títulos de fin de evento del evento diario de Pesca en el Mar.",
    },
  },
  "mystery-onsen-mountain-insect-hunter": {
    "pt-BR": {
      name: "Caçador de Insetos da Montanha Onsen",
      description: "Colete todos os títulos de fim de evento do evento diário de Isca para Insetos.",
    },
    es: {
      name: "Cazador de Insectos de la Montaña Onsen",
      description: "Recolecta todos los títulos de fin de evento del evento diario de Cebo para Insectos.",
    },
  },
};

export const ACHIEVEMENT_TRANSLATIONS: Record<
  string,
  Partial<Record<TranslatedLocale, AchievementTranslation>>
> = Object.fromEntries(
  ACHIEVEMENTS.map((achievement) => [
    achievement.id,
    {
      ...(ACHIEVEMENT_TRANSLATIONS_BASE[achievement.id] ?? {}),
      id: (ACHIEVEMENT_TRANSLATIONS_ID as Record<string, AchievementTranslation>)[achievement.id],
      th: (ACHIEVEMENT_TRANSLATIONS_TH as Record<string, AchievementTranslation>)[achievement.id],
      de: (ACHIEVEMENT_TRANSLATIONS_DE as Record<string, AchievementTranslation>)[achievement.id],
    },
  ]),
);

const HOBBY_LABELS: Record<string, Partial<Record<TranslatedLocale, string>>> = {
  Fishing: { "pt-BR": "Pesca", es: "Pesca", id: "Fishing", th: "ตกปลา" },
  Cooking: { "pt-BR": "Culinária", es: "Cocina", id: "Cooking", th: "ทำอาหาร" },
  Gardening: { "pt-BR": "Jardinagem", es: "Jardinería", id: "Gardening", th: "ทำสวน" },
  "Cat Caring": { "pt-BR": "Cuidado de Gatos", es: "Cuidado de Gatos", id: "Cat Caring", th: "ดูแลแมว" },
  "Dog Caring": { "pt-BR": "Cuidado de Cães", es: "Cuidado de Perros", id: "Dog Caring", th: "ดูแลสุนัข" },
  "Insect Catching": { "pt-BR": "Captura de Insetos", es: "Caza de Insectos", id: "Insect Catching", th: "จับแมลง" },
  Birdwatching: { "pt-BR": "Observação de Pássaros", es: "Observación de Aves", id: "Birdwatching", th: "ดูนก" },
  "Sand Sculpting": { "pt-BR": "Escultura de Areia", es: "Escultura de Arena", id: "Sand Sculpting", th: "ปั้นทราย" },
  "Pumpkin Carving": {
    "pt-BR": "Escultura de Abóbora",
    es: "Talla de Calabazas",
    id: "Pumpkin Carving",
    th: "แกะสลักฟักทอง",
  },
  "Snow Sculpting": { "pt-BR": "Escultura de Neve", es: "Escultura de Nieve", id: "Snow Sculpting", th: "ปั้นหิมะ" },
};

/** Merges the English base achievement with its pt-BR/es override, when one exists. */
export function localizeAchievement(achievement: HeartopiaAchievement, locale: Locale): HeartopiaAchievement {
  if (locale === "en") return achievement;
  const override = ACHIEVEMENT_TRANSLATIONS[achievement.id]?.[locale];
  if (!override) return achievement;
  return {
    ...achievement,
    name: override.name,
    description: override.description,
    titleGained: override.titleGained ?? achievement.titleGained,
    hint: override.hint ?? achievement.hint,
  };
}

/** Translated label for a hobby filter chip/badge; falls back to the raw (English) value. */
export function getHobbyLabel(hobby: string, locale: Locale): string {
  if (locale === "en") return hobby;
  return HOBBY_LABELS[hobby]?.[locale] ?? hobby;
}

const ACHIEVEMENT_FAQ_STRINGS: Record<
  Locale,
  {
    unlockQuestion: (name: string) => string;
    titleQuestion: (name: string) => string;
    titleAnswer: (name: string, title: string) => string;
  }
> = {
  en: {
    unlockQuestion: (name) => `How do I unlock "${name}"?`,
    titleQuestion: (name) => `What title do I get from "${name}"?`,
    titleAnswer: (name, title) => `Completing "${name}" grants the title "${title}".`,
  },
  "pt-BR": {
    unlockQuestion: (name) => `Como eu desbloqueio "${name}"?`,
    titleQuestion: (name) => `Que título eu ganho com "${name}"?`,
    titleAnswer: (name, title) => `Completar "${name}" concede o título "${title}".`,
  },
  es: {
    unlockQuestion: (name) => `¿Cómo desbloqueo "${name}"?`,
    titleQuestion: (name) => `¿Qué título obtengo con "${name}"?`,
    titleAnswer: (name, title) => `Completar "${name}" otorga el título "${title}".`,
  },
  id: {
    unlockQuestion: (name) => `Bagaimana cara membuka "${name}"?`,
    titleQuestion: (name) => `Gelar apa yang didapat dari "${name}"?`,
    titleAnswer: (name, title) => `Menyelesaikan "${name}" memberikan gelar "${title}".`,
  },
  th: {
    unlockQuestion: (name) => `ปลดล็อก "${name}" อย่างไร?`,
    titleQuestion: (name) => `ได้รับฉายาอะไรจาก "${name}"?`,
    titleAnswer: (name, title) => `เมื่อทำ "${name}" สำเร็จ จะได้รับฉายา "${title}"`,
  },
  de: {
    unlockQuestion: (name) => `Wie schalte ich „${name}" frei?`,
    titleQuestion: (name) => `Welchen Titel bekomme ich durch „${name}"?`,
    titleAnswer: (name, title) => `Wer „${name}" abschließt, erhält den Titel „${title}".`,
  },
};

/**
 * Generates FAQ items straight from data already on the achievement — never
 * invented copy. Every achievement page gets an "how do I unlock this"
 * question (from `description`) and, when present, a "what title do I get"
 * question (from `titleGained`), so the FAQPage JSON-LD schema covers all
 * ~90 achievements without hand-writing per-entry content.
 */
export function getAchievementFaq(achievement: HeartopiaAchievement, locale: Locale): FaqItem[] {
  const localized = localizeAchievement(achievement, locale);
  const strings = ACHIEVEMENT_FAQ_STRINGS[locale];
  const items: FaqItem[] = [];
  if (localized.description) {
    items.push({ question: strings.unlockQuestion(localized.name), answer: localized.description });
  }
  if (localized.titleGained) {
    items.push({
      question: strings.titleQuestion(localized.name),
      answer: strings.titleAnswer(localized.name, localized.titleGained),
    });
  }
  return items;
}
